import { css, cx } from "@styled-system/css";
import { button } from "@styled-system/recipes";
import useControl from "@/hooks/useControl";
import { Dialog1, Dialog2 } from "./dialogs";
import BarkioBanner1 from "@/assets/barkio/sep-anxiety/BarkioBanner1.jpg";
import BarkioBanner2 from "@/assets/barkio/sep-anxiety/BarkioBanner2.jpg";
import WaitingDog from "@/assets/barkio/sep-anxiety/WaitingDog.jpg";
import SadDog from "@/assets/barkio/sep-anxiety/SadDog.jpg";
import DigFence from "@/assets/barkio/sep-anxiety/DigFence.jpg";
import SmilingDog from "@/assets/barkio/sep-anxiety/SmilingDog.jpg";
import SadDogBowl from "@/assets/barkio/sep-anxiety/SadDogBowl.jpg";
import DogPhotoShoot from "@/assets/barkio/sep-anxiety/DogPhotoshoot.jpg";
import LeavingDog from "@/assets/barkio/sep-anxiety/LeavingTheDog.jpg";
import DogMonitoring from "@/assets/barkio/sep-anxiety/DogMonitoring.jpg";
import Infographic from "@/assets/barkio/sep-anxiety/infoGraphic.jpg";
import TT1 from "@/assets/barkio/sep-anxiety/TT1.jpg";
import TT2 from "@/assets/barkio/sep-anxiety/TT2.png";

export const listItems = [
  "what-is-separation-anxiety",
  "how-does-fear-originate",
  "anxiety-symptoms",
  "differential-diagnoses",
  "what-to-watch",
  "diagnostics",
  "treatment",
  "patient-monitoring",
  "prevention",
  "prognosis",
  "stories",
  "about-us"
];

export default function Data() {
  const dialog1 = useControl();
  const dialog2 = useControl();

  return (
    <div>
      <Dialog1 isOpen={dialog1.isOpen} onClose={dialog1.close} />
      <Dialog2 isOpen={dialog2.isOpen} onClose={dialog2.close} />
      <div>
        <div className="chapter-wrap" id="what-is-separation-anxiety">
          <div className="chapter-number">01</div>
          <h3>What is separation anxiety?</h3>
        </div>
        <p>
          Separation anxiety is a state of distress and fear experienced by a dog when a person the dog is strongly
          attached to leaves the house or is just about to leave. It is the most common issue that dogs and their owners
          face. Dogs of any breed (including crossbreeds) and either gender may develop separation anxiety. This
          disorder mostly affects young dogs who are three or less than three years of age (approximately 60%); the
          occurrence of separation anxiety reduces in their middle age. It is more common again when their age is around
          8. This is most possibly connected to other anxiety disorders that are usually experienced by dogs in their
          old age.
        </p>
        <a href="/">
          <img src={BarkioBanner1.src} alt="Barkio app" className="article-image banner" />
        </a>
        <h4 id="reasons">Causes of origin</h4>
        <p>
          A dog is a strongly social animal that needs the company of other social partners to live a happy life.
          Solitude can often create fear, anxiety, and uncertainty in them.
        </p>
        <p>
          Dogs who deal with solitude well are usually the ones with an immune nervous system that enables them to learn
          to be alone gradually. It is less likely for a dog to develop separation anxiety if it has enough social
          contact with other dogs and with people outside the family. Moreover, they should not be exposed to long
          periods of separation and should be protected from stressful events, such as storms, fireworks, or even loud
          noises in the house.
        </p>
        <p>
          It is very difficult for puppies older than 16 weeks or for mature dogs that are taken away from their pack to
          get used to the solitude that they had experienced when they lived with their mother or siblings. The ones
          likely to develop separation anxiety are also the ones who come from shelters or those rescued from the
          streets or cruel conditions. Even the shortest periods or just a hint of the possibility that they might be
          left alone may cause them to panic. Separation anxiety also affects dogs with unstable nervous systems or
          tendencies to experience other fears and phobias (these tendencies are usually inherited).
        </p>
        <div className="custom-quotation">
          <div className="custom-line col-lg-3"></div>
          <p>
            “It is the most common issue that dogs and their owners face. Dogs of any breed (including crossbreeds) and
            having either gender may develop separation anxiety.”
          </p>
        </div>
        <img src={WaitingDog.src} alt="Dog waiting" className="article-image" />
        <p>
          Many factors can cause separation anxiety. The following ones are those that are most commonly connected to
          the development of anxiety:
        </p>
        <h5 id="single">If the owner lives alone with the dog</h5>
        <p>
          This phenomenon is connected to the fact that these dogs may have to spend extended periods of time alone (8
          hours or more) when the owner is at work. It may also occur in dogs who live in the company of elderly persons
          who only leave them behind on rare occasions for a brief period (when they go to the doctor or go shopping).
        </p>
        <h5 id="void">No puppy school or proper training ground nearby</h5>
        <p>
          Dogs that are used to “work” with the owner, that is, those dogs who follow their owners’ instructions and
          desire reward and recognition often face problems getting used to solitude.
        </p>
        <h5 id="hotel">Dogs staying in a hotel</h5>
        <p>
          Separation anxiety may occur when a dog stays in an unknown environment of a dog hotel or some other facility
          when the owner cannot take care of it.
        </p>
        <h5 id="scare">Going through a frightening experience</h5>
        <p>
          A triggering moment might be a situation when the dog experiences something scary when it is at home on its
          own. This includes an intense storm, draft, broken window, robbery, or the noisy reconstruction of a
          neighbor’s flat.
        </p>
        <h5 id="puppy">Puppy rearing</h5>
        <p>
          One of the causes that lead to the development of separation anxiety is the experience of puppies being
          brought up in inadequate conditions, such as a shed, garage, separate room from that of their owners, or a
          crate, where they lack outside impulses. These dogs are often oversensitive to any sort of stress, including
          solitude, and it is harder to train them to get accustomed to solitude.
        </p>
        <div id="how-does-fear-originate" className="chapter-wrap">
          <div className="chapter-number">02</div>
          <h3>How does the fear originate?</h3>
        </div>
        <p>
          Fear is one of the emotions that originate primarily in an almond-shaped mass of nuclei called the amygdala.
          The amygdala is located in the temporal lobe of the brain, and all the information regarding a stimulus as
          well as all possible experience associated with it can be found there. It reacts to a stimulus by sending a
          signal to the following three locations: 1) the grey matter that controls the activity of the skeletal and
          muscular system; 2) the parts of the hypothalamus that control the activity of the autonomic nervous system,
          and 3) the parts of the hypothalamus that control hormonal function. It triggers an instantaneous reaction in
          the whole body that is aimed at saving the life. This reaction is called the fight-or-flight response, and it
          is characterized by increased breathing and heart rates, increased cardiac output, and increased blood flow to
          vital organs (brains, muscles). The hormonal response involves the release of cortisone, which significantly
          impacts glucose metabolism and other important metabolic processes. The body mobilizes quick sources of
          energy.
        </p>
        <p>
          In some cases, these changes are positive because they allow the animal to fight or run away from the danger.
          When the animal cannot escape or avoid it in any way, a chronic stress reaction appears, which might end up
          harming or even killing the animal.
        </p>
        <img src={SadDog.src} alt="Sad Dog" className="article-image" />
        <p>
          The character of the response to a fear impulse is dependent on several factors and can be classified as
          genetic and empiric. In most cases, the animal reacts normally to impulses that threaten it at that moment.
          When the reaction is extensive or nonsensical, it is considered to be abnormal. It may lead to dysregulation –
          loss of control over responses to impulses – that can show up on several levels, including neurotransmitter
          activities. Neurotransmitters are chemical substances that transmit signals and information between nerve
          cells. The most important neurotransmitters that participate in the response to fear are serotonin,
          noradrenaline, and gamma-aminobutyric acid (GABA). Therefore, the treatment of disorders related to fear
          focuses on influencing these neurotransmitters.
        </p>
        <div className="space-left">
          <div className="quoteline-side">
            <div className="custom-quotation2">
              <p>
                <span className="color-quote">Neurotransmitters </span>are chemical substances that transmit signals and
                information between nerve cells. The most important neurotransmitters that participate in the response
                to fear are serotonin, noradrenaline, and gamma-aminobutyric acid (GABA). Therefore, the treatment of
                disorders related to fear focuses on influencing these neurotransmitters.
              </p>
            </div>
          </div>
        </div>
        <div id="anxiety-symptoms" className="chapter-wrap">
          <div className="chapter-number">03</div>
          <h3>Symptoms of separation anxiety</h3>
        </div>
        <p>
          There are many forms in which separation anxiety is exhibited. It is vital for the owner to ensure that the
          dog that suffers from separation anxiety never does anything harmful on purpose or avenges, even though this
          is what most owners think, unfortunately.
        </p>
        <h5>Destroying house equipment</h5>
        <p>
          Dogs most commonly destroy things that are connected to the person to who they are close – beds, couches,
          books, bags, shoes, clothes or kitchen items. They often destroy walls and floors around windows and doors by
          intense pawing and scratching.
        </p>
        <h5>Attempts to escape</h5>
        <p>
          The dog is relentlessly trying to escape, so it keeps scratching and biting the door and window frames. It can
          also try to dig under the fence or gnaw through a wire fence. The dog chews and bends bars of the crate, jumps
          or climbs over tall fences and walls or tries to jump through a closed window. There is a high risk of serious
          injuries occurring in the course of these desperate acts.
        </p>
        <img src={DigFence.src} alt="Dog escaping" className="article-image" />
        <h5>Barking and howling</h5>
        <p>
          It mostly begins immediately after the dog is convinced that the owner has left. A door closing or a departing
          car work as signals. With small pauses for listening to whether the owner is coming back, the dog can bark and
          howl for many hours. It is not unusual for the dog to do it the entire time the owner is not at home.
        </p>
        <div className="custom-quotation">
          <div className="custom-line col-lg-3"></div>
          <p>
            ‘The dog does not do this “to spite you”, as many angry dog owners claim; it is simply an involuntary
            reaction of the dog's body triggered by the fear.’
          </p>
        </div>
        <h5>Impulsive excretion</h5>
        <p>
          When the fear kicks in the metabolism of the dog, it accelerates bladder as well as rectum function, causing
          them to fill up in a very short time – often in several minutes. The sensitivity of the bladder rises to such
          a level that the dog feels a compulsion to excrete even when the bladder is not full. Fear also causes the
          sphincter to relax and therefore, the dog cannot hold its urine or feces in the manner it can when it is calm
          and has to empty the bladder and/or bowels immediately, regardless of where it is. The dog does not do this
          ‘to spite you’, as many angry dog owners claim; it is simply an involuntary reaction of the dog's body
          triggered by fear.
        </p>
        <h5>Strong salivation</h5>
        <p>
          The fact that the dog drools extensively can also be the animal’s reaction to great fear and anxiety.
          Sometimes, it can drool to such an extent that the dog gets all wet and the floor becomes slippery.
        </p>
        <h5>Accelerated and difficult breathing</h5>
        <p>
          This symptom is connected to the fear reaction of the animal. The dog has an open snout, the tongue sticking
          out, breathing quickly as well as loudly and, sometimes, can even choke. Remaining in such a state for a long
          period of time can lead to complete exhaustion.
        </p>
        <h5>Inappetence</h5>
        <p>
          When the dog is by itself, it does not eat or drink. As soon as the owner comes back home, it starts to gobble
          everything, which frequently results in vomiting.
        </p>
        <h5>Stereotypical motion activities</h5>
        <p>
          The dog runs in circles, by the fence or from one door to another for no specific reason. This sort of
          behavior leads to the dog getting tired or completely exhausted.
        </p>
        <h5>Self-harm</h5>
        <p>
          The dog can harm itself while trying to escape a flat or a crate. It is not irregular to develop a compulsive
          behavior such as when the stressed dog starts to lick its paws, sometimes for many hours. Infected wounds
          might appear on these spots that go deep into the muscles or even bones. These wounds are hard to heal as the
          dog licks them over and over again when it experiences stress.
        </p>
        <h5>Following the close person everywhere</h5>
        <p>
          The dog goes through immense anxiety due to the prospect of the owner leaving again, so it fears losing visual
          contact with them even for a brief moment. It nervously follows the owner all around the flat, checking
          whether he/she is about to go somewhere. The dog cannot relax and calm down, which exhausts it.
        </p>
        <h5>Signs of anxiety when the owner is about to leave</h5>
        <p>
          As soon as the dog spots the first signs of the owner leaving, it starts to shiver, salivate, breathe fast,
          refuse to go to its designated place, and will not even eat anything. Some dogs become aggressive when the
          owner is leaving and do not let him/her leave from the door or gate.
        </p>
        <h5>Inactivity and hiding</h5>
        <p>
          A scared dog sometimes tries to hide itself to avoid an activity, such as a morning walk, that is often
          followed by the owner’s departure. Some dogs try to hide under a parked car or a place hard to access. Some
          just do not allow their owner to catch them when they have to let them out before leaving.
        </p>
        <h5>Hectic welcoming</h5>
        <p>
          As soon as the owner returns, the dog starts to welcome him/her vigorously. It jumps around and breathes with
          an open snout and its tongue out. It also whines sometimes, and it takes time to fully calm down. This kind of
          intense welcoming may occur even after a very short period in which the owner was gone.
        </p>
        <img src={SmilingDog.src} alt="Dog smiling" className="article-image" />
        <div className="warning-box">
          <h6>ATTENTION!</h6>

          <p className="warning-txt">
            Many of these symptoms might not necessarily mean separation anxiety. You can read about differential
            diagnosis below.
          </p>
        </div>
        <div id="differential-diagnoses" className="chapter-wrap">
          <div className="chapter-number">04</div>
          <h3>Differential diagnoses</h3>
        </div>
        <p>
          Through this term, we classify other illnesses and conditions that have similar symptoms as separation anxiety
          but are different, and it is necessary to differentiate them from anxiety.
        </p>
        <p>
          Young, active dogs especially have tendencies{" "}
          <span className="bold">to damage home equipment and chew on things</span> only to entertain themselves. They
          can also do it because of the gum pain they experience during teeth replacement or diseases of the teeth and
          gums.
        </p>
        <p>
          <span className="bold">Attempts to escape or scratching around doors and windows </span>
          might have hormonal origins (female on heat) or the dog has tendencies to wander. It could also be frustrated
          with the limited space or even be suffering from claustrophobia.
        </p>
        <p>
          <span className="bold">Howling, barking and whining may be only reactions to external stimuli </span>
          such as other people’s or animals’ motion in the corridor or around the house, noises caused by a loud
          reconstruction in the apartment next door or ongoing construction work close by. It can also be caused by
          something hurting or bothering the dog (heat, cold, thirst, hunger). Another cause is a cognitive disorder in
          older dogs who tend to forget learned habits and react inadequately.
        </p>
        <p>
          <span className="bold">Urination and excremination</span> are caused by several reasons. There can be an issue
          with the digestive system or excretory system or the dog has poor hygiene habits. It can also be caused by a
          storm, fireworks etc. There are also possibilities of incontinence, marking or cognitive disorders in older
          dogs.
        </p>
        <p>
          <span className="bold">Strong salivation</span> might occur if the dog drank detergent, irritant or a bitter
          substance, or even if it managed to poison itself. Teeth or gums illness might also cause extensive
          salivation.
        </p>
        <p>
          <span className="bold">Accelerated and heavy breathing</span> may occur under the influence of hormones or
          external stimuli. Another reason for this can be when the dog overheats or is battling a respiratory system
          disease, circulatory disorder or an illness with an infection or high temperature.
        </p>
        <p>
          <span className="bold">Inappetence</span> is usually a sign of an overall illness, oral cavity illness or
          digestive system illness. The general appetite of a dog is highly influenced by hormones during the period of
          time when females are in heat. Appetite might also be reduced when the weather is too warm or the dog fears
          something other than separation.
        </p>
        <img src={SadDogBowl.src} alt="Dog with bowl" className="article-image" />
        <p>
          <span className="bold">Stereotypical motion activities</span> do not always mean separation anxiety. It might
          indicate a different fear or anxiety, boredom or a primary psychological illness and pain.
        </p>
        <p>
          <span className="bold">Self-harm</span> can be caused by a dermatological problem (skin disease), injury or
          allergy. Another reason for a dog to harm itself is boredom or, again, a primary psychological illness –
          compulsive disorder.
        </p>
        <div id="what-to-watch" className="chapter-wrap">
          <div className="chapter-number">05</div>
          <h3>What is important to watch and why?</h3>
        </div>
        <p>
          To watch the dog, know about its atypical symptoms and discuss them with veterinarians is absolutely crucial.
          It is the only way to properly diagnose separation anxiety.
        </p>
        <img src={DogPhotoShoot.src} alt="Dog being monitored " className="article-image" />
        <h5>Which persons and animals are members of the home?</h5>
        <p>
          There are people who the dog does not feel as strongly about, and it does not miss them as much as the
          person(s) it feels to be the most important to be present. It is not necessarily the head of the family as we
          perceive it.
        </p>
        <h5>Does the dog show big dependence on one person?</h5>
        <p>
          An affected dog is usually “glued” to the one it has the strongest bond with. The dog lies on the person’s
          feet or next to him/her on the couch, follows him/her around the house, cannot relax in another room and jumps
          up anytime it feels the owner might be about to leave.
        </p>
        <h5>What is the household’s daily regime?</h5>
        <p>
          It is ideal for the dog that there be rituals that keep the dog informed that everything is as it should be.
          Irregular work shifts, occasional evening absence of the owner or business trips when the dog is alone or is
          watched by unfamiliar people for a long period of time are all risky occurrences.
        </p>
        <h5>What is the regime of feeding, walking, playing and exercising?</h5>
        <p>
          All of this information is important for the owner to plan training processes accordingly. It is easier to
          acclimatize the dog to solitude when it is used to working with its owner and motivated to learn something
          new. Becoming accustomed to solitude is difficult for a dog that never cooperates with its owner.
        </p>
        <p></p>
        <h5>What does problematic behavior look like?</h5>
        <p>
          It is vital to consider all the details through the perspective of time – how much in advance does the dog
          start to behave nervously, what does it look like, when is the anxious behavior at its strongest and whether
          the symptoms gradually go away or are equally intense the whole time the owner is gone.
        </p>
        <h5>When did this behavior first occur?</h5>
        <p>
          Some dogs that have been brought home as grown puppies or were adopted from a shelter may exhibit anxious
          behavior from the very early days during which they form a strong bond with the owner. It is common for
          separation anxiety to be preceded by the owner being home for extended periods (illness, maternity leave,
          unemployment) or, on the other hand, the owner’s absence (holiday, long business trip, hospital stay). It can
          also be developed after getting separated from a dog or a cat it was used to while at home.
        </p>
        <h5>Under what conditions does this behavior occur?</h5>
        <p>
          Is the dog calm when a familiar person is at home? Does it tolerate at least short “ritual” departures such as
          going shopping (symbolized by a shopping bag) or going out with a garbage bag?
        </p>
        <h5>How does the owner react?</h5>
        <p>
          Does he/she scold or beat the dog because of broken things or wet carpets? Does the owner put the dog in a
          cage, tie it or give it an electric collar?
        </p>
        <h5>How does the dog typically welcome the owner and how does he/she react?</h5>
        <p>
          Is the welcoming very hectic, involves jumping, whining and accelerated breathing? Does the dog look guilty,
          crouch, hide or lay on its back?
        </p>
        <h5>What are the habits before the departure and how does the dog react?</h5>
        <p>
          The dog starts becoming nervous when it spots the first signs that usually lead to the owner leaving. Does it
          react to all the departures the same way or different when it happens in the evening, on weekends, etc? Is it
          different from the behavior during the week? Does the dog react differently when family members leave one by
          one as compared to when they leave together? Is the dog calm during some members’ departure and anxious during
          the others’?. How long after the owner’s departure does the dog start to show symptoms of stress? It is
          typical in cases of separation anxiety that the dog becomes anxious before the solitude itself, and the
          biggest stress comes just after or soon after the departure.
        </p>
        <h5>Where is the dog while the owner is gone?</h5>
        <p>
          Can the dog use the whole flat or house to move around when the owner is not home? Is the dog’s space limited?
          How does the dog react when it is being put in that limited space?
        </p>
        <h5>How does the dog behave while left in a different environment and with other people?</h5>
        <p>
          Is it possible for the dog to stay calm when left with relatives or friends? Does it eat and drink? Does it
          rest? Does it play? Does it communicate?
        </p>
        <h5>Does the dog get along well with other dogs?</h5>
        <p>
          This is vital to know in cases where the dog spends some time in a dog hotel or any other similar facility
          with other dogs during his therapy.
        </p>
        <h5>Does the dog show signs of other fear or phobia?</h5>
        <p>
          Unstable dogs that suffer from fear of storms, fireworks or similar stimuli often suffer from separation
          anxiety as well. Treatment of separation anxiety in these dogs is time-consuming, and it is common for them to
          never fully recover from this anxiety.
        </p>
        <div id="diagnostics" className="chapter-wrap">
          <div className="chapter-number">06</div>
          <h3>How to diagnose separation anxiety?</h3>
        </div>
        <p>
          When the owner brings the dog to the vet, it does not behave in a manner that would allow the doctor to
          diagnose separation anxiety. The vet is dependent on the information he/she gets from the owner. This
          information might not have to be perfectly precise and complete.
        </p>
        <p>
          The best way to diagnose is to have a video recording that shows the dog’s behavior during a certain time.
          Based on the symptoms the dog shows in the recording, the vet is able to diagnose the problem either
          immediately or after thorough examinations of all possible systems (urinary, excretory, circular, nerve or
          skin) by laboratory or imaging methods.
        </p>
        <p>The doctor needs to be familiar with the overall state of health of the patient.</p>
        <a href="/">
          <img src={BarkioBanner2.src} alt="Barkio app" className="article-image banner" />
        </a>
        <div id="treatment" className="chapter-wrap">
          <div className="chapter-number">07</div>
          <h3>Treatment</h3>
        </div>
        <h4>a) Understanding the substance of the problem</h4>
        <p>
          The first thing to realize is that the dog does not do it to spite anyone as revenge for being left alone. The
          dog is just scared and cannot cope with the solitude. Any form of punishment, even scolding, will only make
          the matter worse, as then the dog fears solitude as well as the return of the owner. When the dog howls or
          destroys something (or excretes), it does not realize it is doing something bad. It is simply its reaction to
          fear. Therefore, there is no place whatsoever for physical punishment during anxiety treatment. The treatment
          itself might take long. It could be months in some cases.
        </p>
        <p>
          Symptoms may return when the owner leaves for a couple of days or is at home all the time and then leaves to
          work. It may also reappear if the working hours change or the daily regime in the household changes.
        </p>
        <div className="custom-quotation">
          <div className="custom-line col-lg-3"></div>
          <p>
            “The dog is just scared and cannot cope with the solitude. Any form of punishment, even scolding, will only
            make the matter worse, as then the dog fears solitude as well as the return of the owner.“
          </p>
        </div>
        <h4>b) Adjustment of conditions and environment</h4>
        <p>
          The first thing to do is to eliminate the fear stimulus. This means that the dog should not be left alone
          during the time of the treatment. It should either stay with the owner or at a place where it does not show
          any signs of anxiety (stationary, hotel, with relatives, in the car or in the garden). For a better
          understanding of this, it is possible to use an example of burns: you first pull the hand out of the fire, and
          only then it can start healing.
        </p>
        <p>
          The environment should be adjusted so that the dog feels well and is safe (preventing possibilities to escape,
          getting injured or self-harm). Some dogs like to have a lot of room around, some prefer the crate. Everyday
          “departure training” is important. The training should be preceded by a little walk with games and exercises
          ended with praise and calming. The dog should not be given food before training (departure).
        </p>
        <h4>c) Behavior modification training</h4>
        <p>
          Just as with other fear-related disorders, even separation anxiety is treated with the help of{" "}
          <span className="bold">desensitization</span> (very briefly exposing it to the stimulus so that the fear does
          not manage to occur, the time of exposure is gradually prolonged) and{" "}
          <span className="bold">counter-conditioning</span> (teaching new conditional reflexes/new responses to an old
          stimulus). Based on the character and intensity of the condition, the owner performs either all or just some
          of the points listed below:
        </p>
        <p>
          <span className="bold">Independence training:</span> The owner does not react to the dog trying to get the
          owner’s attention. He/she starts and finishes common activities and rewards independent behavior (being alone
          in its place or being in a different room than the owner). The dog is separated from the owner but only for as
          long as it is able to stay calm.
        </p>
        <p>
          <span className="bold">Changing the meaning of departure rituals:</span> The owner performs the usual rituals
          (keys, bag, clothes, perfume etc.), goes towards the doors but will not leave, and instead returns to the
          previous activity. This is repeated twice or thrice a day until the dog is calm. Pauses between the ritual
          pieces of training must be long enough so that the dog can completely calm down. The goal of this is to have
          the dog stop connecting the rituals only with departure and a long absence of the owner.
        </p>
        <img src={LeavingDog.src} alt="Leaving the Dog alone" className="article-image" />
        <p>
          <span className="bold">Change of departure and return habits:</span> The owner pays the dog no attention for
          5–15 minutes before departure and does not “say goodbye”. When returning the owner greets the dog with no
          emotion and immediately takes the dog out.
        </p>
        <div className="space-left">
          <div className="quoteline-side">
            <div className="custom-quotation2">
              <p>
                <span className="color-quote">Desensitization </span>is a very brief exposure to the stimulus so that
                the fear does not manage to occur, with the time of exposure being gradually increased.
              </p>

              <p>
                <span className="color-quote">Counter-conditioning </span>is teaching a new conditional reflex/new
                response to an old stimulus.
              </p>
            </div>
          </div>
        </div>
        <p>
          <span className="bold">Departure counter-conditioning:</span> The owner teaches the dog to stay in a place
          where it is not afraid and is able to relax. He/she creates a correlation between training departures/short
          departures and something positive, such as good food in a filling toy. If the owner has to leave for a longer
          period of time than the dog is used to, it should not be left in the place where training take place and
          should not play with the same toys. A dog should go through 1–2 training sessions a day.
        </p>
        <p>
          The time the dog can stay alone without stress is set in the beginning. It can be seconds or minutes. After
          seconds (minutes), the time during which the dog is alone is increasingly prolonged. Departures and returns
          are being done without emotions and without paying attention to the dog. The owner gives the dog a filling toy
          before he/she leaves. When the dog gets up and tries to follow the owner, he/she comes back and takes the toy
          away from the dog. The training continues until the dog fully calms down and is interested in the toy again.
        </p>
        <p>
          Departures are only indicated at first or the owner only goes to the next room. Only when the dog endures
          these indicated departures well, the owner should try to open the door to the corridor, step out and
          immediately come back. He/she should try to close the door and open it right away. The owner should then leave
          home for a couple of seconds or minutes and progressively prolong the time of absence by 1–2 minutes. He/she
          should gradually start to behave the same way as when he/she is actually leaving – takes the bag, keys, puts
          on shoes, etc. If the dog is still nervous, it is necessary to come back to the point where the dog was calm.
        </p>
        <p>
          As soon as the dog is able to be alone for 30 minutes, the period may be prolonged by 5–10 minutes. If the dog
          is able to stay alone for 2–3 hours, the training is no longer necessary. It should not stay alone longer than
          it is comfortable though.
        </p>
        <div className="warning-box">
          <h6>ATTENTION</h6>
          <ul>
            <li>
              <p className="warning-txt">
                <span className="bold">Incorrectly applied behavioral therapy</span> may lead to more fear. If the owner
                continues to train departure and absence even if the dog expresses stress and anxiety, the conditions
                will get worse.
              </p>
            </li>

            <li>
              <p className="warning-txt">
                <span className="bold">Crate training </span>can only be done with dogs that seek small spaces and feel
                good in them. The condition of a dog that does not seek small spaces will only get worse if this method
                is applied.
              </p>
            </li>

            <li>
              <p className="warning-txt">
                <span className="bold">Getting another pet</span> as a companion is not recommended, it rarely works.
              </p>
            </li>
          </ul>
        </div>
        <h4>d) Medicaments</h4>
        <p>
          Lighter forms of separation anxiety might be treated by thorough training without medicaments being necessary.
          It is easier to do it with them though. They reduce fear and anxiety and they are safe and available. These
          include mainly medicaments that contain
          <span className="bold"> synthetic soothing dog pheromones</span> such as vaporizers and dog collars as well as
          <span className="bold"> dietary supplements</span> containing L-tryptophan or alpha-casozepine or
          alternatively, a complex veterinary diet enriched by these compounds.
        </p>
        <p>
          Pharmacologic treatment is aimed at the neurotransmitters (chemical messengers) that take part in the fear and
          anxiety responses: serotonin, noradrenaline, dopamine and GABA. These medicaments are prescribed by the vets
          after a thorough examination of the patient. The doctor then monitors their efficiency and side effects.
        </p>
        <div id="patient-monitoring" className="chapter-wrap">
          <div className="chapter-number">08</div>
          <h3>Patient monitoring</h3>
        </div>
        <p>
          The best way to determine the progress of the treatment is to monitor the dog during the time it is by itself.
          That can be done by setting up a video camera or with the help of{" "}
          <a
            id="dog-link-treatment"
            target="_blank"
            href="http://www.barkio.com/?utm_source=separation_anxiety_web&amp;utm_campaign=separation_anxiety_web"
            className="DMlink"
          >
            Barkio
          </a>
          . This convenient phone app allows the owner to have both visual and audio overviews of what his/her dog goes
          through when left home. That helps the owner adjust the training and also gives the vet data that makes it
          easy to diagnose and decide whether to start medical treatment. Medical treatment should last one more month
          after successful treatment. Thereafter, ¼ of the medicaments is dropped per week while constant monitoring for
          the potential return of the symptoms.
        </p>
        <img src={DogMonitoring.src} alt="Leaving the Dog alone" className="article-image" />
        <div id="prevention" className="chapter-wrap">
          <div className="chapter-number">09</div>
          <h3>Prevention</h3>
        </div>
        <p>
          Prevention refers to the slow and steady process of getting the puppy used to solitude. To increase the
          chances of success, it is recommended to leave the puppy in a nice and safe environment, walk it, make it
          exercise before leaving and leave the reward (filling toy) while leaving. The process of both leaving and
          coming back should only be accompanied by a short greeting.
        </p>
        <p>
          <span className="bold">How to leave for work:</span> It is the dramatic departure that makes the dog feel that
          being alone without the owner is a big deal. Therefore, it is highly recommended to leave the house without
          exaggerative petting. This also applies to the process of coming back home. It is good to turn the dog’s focus
          to something entertaining, like a toy.
        </p>
        <img src={Infographic.src} alt="How to say goodbye to your dog" className="infographic" />
        <p>
          It is good to monitor the puppy while it is alone from the very beginning so that the duration of the solitude
          and equipment in the environment can be adjusted as per the physiological needs (food, excretion) and nature
          (toys, crates) of the puppy. It can capture the first symptoms of stress and fear so that the problem can be
          coped with immediately and the chance of conducting a successful therapy increases.
        </p>
        <p>
          This is where the{" "}
          <a
            id="dog-link-prevention"
            className="DMlink"
            target="_blank"
            href="http://www.barkio.com/?utm_source=separation_anxiety_web&amp;utm_campaign=separation_anxiety_web"
          >
            Barkio
          </a>{" "}
          phone app comes in handy. It allows the owner to see how the dog behaves when left at home alone. The
          practical activity log feature shows the history of monitoring during the time it was left alone. It helps
          both the owner and the vets to map the dog's behavior during a certain period of time, which gives the
          opportunity to determine whether or not the animal indeed suffers from separation anxiety.
        </p>
        <div className="custom-quotation">
          <div className="custom-line col-lg-3"></div>
          <p>
            “This is where the{" "}
            <a
              id="dog-link-prevention-quote"
              className="DMlink"
              target="_blank"
              href="http://www.barkio.com/?utm_source=separation_anxiety_web&amp;utm_campaign=separation_anxiety_web"
            >
              Barkio
            </a>{" "}
            phone app comes in handy. It allows the owner to see how the dog behaves when left at home alone. The
            practical activity log feature shows the history of monitoring during the time it was left alone.”
          </p>
        </div>
        <p>
          Another way to prevent a dog from developing separation anxiety is to keep it active and entertained. When a
          dog does sport activities with their owner such as canicross, it directs their energy and mental focus in the
          right direction instead of escape attempts or tearing a couch. This is especially relevant just before leaving
          the house. When the owner leaves the dog home physically and mentally satisfied, the dog has no capacity and
          energy to be feeling lonely.
        </p>
        <div id="prognosis" className="chapter-wrap">
          <div className="chapter-number">10</div>
          <h3>Prognosis</h3>
        </div>
        <p>
          <span className="bold">Separation anxiety is often relatively easy to solve </span>
          with a low or no level of medicine therapy if the owner cooperates properly. Medicaments can speed up the
          process and relieve the dog from a lot of fear and anxiety. There are some cases though where all training and
          treatment led nowhere. These are usually the cases of long-lasting untreated (or incorrectly treated)
          anxieties or the occurrence of several parallel behavioral disorders together.
        </p>
        <div id="stories" className="chapter-wrap">
          <div className="chapter-number">11</div>
          <h3>Stories</h3>
        </div>
        <p>
          Here you will find true stories of dog patients suffering from separation anxiety and how the condition was
          solved under an expert’s supervision.
        </p>
        <div id="card1" className="story-card" onClick={dialog1.open}>
          <img src={TT1.src} alt="bibi thumbnail" className="card-image" />

          <div className="card-contents">
            <h4 className="card-heading">Bibi ate her own feces while alone</h4>

            <p className="card-txt">
              <span className="card-break">Bibi was a seven-month-old female French Bulldog. </span>
              She lived in a city apartment with her retired owners who were with her most of the time. Bibi was very
              nifty and obedient. She learned many tricks and got on well with the owners’ grandchildren, but she had a
              problem. As soon as she was home alone, even if for only ten minutes, she would immediately urinate and
              defecate and then she would eat those feces. She was never left alone for more than two hours.
            </p>

            <button
              className={cx(
                button({ variant: "outlined" }),
                css({
                  border: "none",
                  ml: "auto",
                  display: "block",
                  mb: "0.5rem"
                })
              )}
            >
              SHOW
            </button>
          </div>
        </div>
        <div id="card2" className="story-card" onClick={dialog2.open}>
          <img src={TT2.src} alt="rocky thumbnail" className="card-image" />

          <div className="card-contents">
            <h4 className="card-heading">Rocky whined and howled while alone</h4>

            <p className="card-txt">
              <span className="card-break">Rocky was a fifteen-month-old active and chipper dog. </span>
              Every terrier likes to bark a lot though. He was taken home by his new owner Monica when he was nine weeks
              old. She would take him to puppy school and training sessions on a regular basis. Rocky liked to fetch, so
              Monica started to train him for flyball. The plans were forced to change due to Monica’s injury. She fell
              down skiing and badly broke her leg. She had to stay home for almost two months. During that time, she and
              Rocky became very close; they spent all the time together at home, playing and resting together. Rocky was
              only taken out to walk by Monica’s boyfriend or her mother.
            </p>

            <button
              className={cx(
                button({ variant: "outlined" }),
                css({
                  border: "none",
                  ml: "auto",
                  display: "block",
                  mb: "0.5rem"
                })
              )}
            >
              SHOW
            </button>
          </div>
        </div>
        <p>
          <span className="bold">
            Does your dog have a similar story? Send it to us at{" "}
            <a id="our-email" href="mailto:support@tappytaps.com?Subject=Separation%20anxiety">
              support@tappytaps.com
            </a>
            , and selected stories may receive one of our apps.
          </span>
        </p>
        <div id="about-us" className="chapter-wrap">
          <div className="chapter-number">12</div>
          <h3>About us</h3>
        </div>
        <h4 id="us">Creators of Barkio and Dog Monitor apps</h4>
        <p>
          They came up with the idea for the{" "}
          <a
            id="dog-link-us"
            className="DMlink"
            target="_blank"
            href="http://www.dogmonitorapp.com/?utm_source=separation_anxiety_web&amp;utm_campaign=separation_anxiety_web"
          >
            Dog Monitor
          </a>{" "}
          app after being approached by some of the users of a similar app called{" "}
          <a id="baby-link-usen" className="DMlink" target="_blank" href="https://www.babymonitor3g.com/">
            Baby Monitor 3G
          </a>
          . To their surprise, the users told them they were using{" "}
          <a id="baby-link-usen-ii" className="DMlink" target="_blank" href="https://www.babymonitor3g.com/">
            Baby Monitor 3G{" "}
          </a>
          to monitor their dogs and that it helps the dogs overcome separation anxiety. Thus, the creators developed a
          “dog version”. In 2020, they launched{" "}
          <a
            id="dog-link-us"
            className="DMlink"
            target="_blank"
            href="http://www.barkio.com/?utm_source=separation_anxiety_web&amp;utm_campaign=separation_anxiety_web"
          >
            Barkio
          </a>
          , a new generation of dog monitoring apps.
        </p>
        <p>
          They started to learn more about the issue of separation anxiety. After discussions with several veterinarians
          and dog psychologists, they adjusted the app so it becomes more effective at resolving separation anxiety. In
          cooperation with experts led by MVDr. Zertova, they put together all their knowledge of anxiety and created
          this online guide.
        </p>
        <h4 id="hana-zertova">MVDr. Hana Zertova</h4>
        <p>
          She graduated from the University of Veterinary and Pharmaceutical Sciences in Brno. She worked as an editor
          in magazines such as Veterinary, Veterinary Clinic and Zodiac and later published her own pets and health
          magazine. Mrs. Zertova also worked in a small-animals doctor’s office.
        </p>
        <p>
          She has spent the last fifteen years focusing exclusively on behavior disorders in dogs and cats. Hana Zertova
          gained her knowledge in this area during her postgraduate studies in Germany. In 2009, she worked on her TV
          series “Pets’ Reformatory”. The doctor wrote a book called “From a puppy to a dog” dealing with the correct
          way of raising puppies.
        </p>
        <h4 id="literature">Literature</h4>
        <p>During the preparation of this website, we have mainly used these resources:</p>
        <ul>
          <li>
            <p>
              <span className="bold">Schmidt W-D:</span> Verhaltenstherapie des Hundes. Schlutersche, Hannover, 2002.
            </p>
          </li>
          <li>
            <p>
              <span className="bold">Horwitz DF, Neilson JC:</span> Canine and Feline Behavior. Blackwell Publishing,
              Ames 2007.
            </p>
          </li>
          <li>
            <p>
              <span className="bold">Bennet SL:</span> Animal Behavior Case of the Month. JAVMA 234, 12, 2013:
              1697-1699.
            </p>
          </li>
        </ul>
        <h4 id="mediakit">Media kit</h4>
        <p>
          Do you find this website useful? Would you like to spread the word about it? Download all the texts and
          graphics about Dog Separation Anxiety Guide{" "}
          <a
            id="download-mediakit"
            href="https://www.dropbox.com/sh/x95cg1f48ecok0c/AACB08zxAZiXtilabO5I5zZ2a?dl=0"
            className="DMlink"
          >
            here
          </a>
          .
        </p>
      </div>
    </div>
  );
}
